import { useEffect, useState } from "react";
import { BarChart3 } from "lucide-react";
import { SectionHead } from "./pioneers/shared";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const RZ = "#D8B23E", PP = "#8E5BD6";
const monthLabel = (m) => { const [y, mo] = m.split("-"); return new Date(Number(y), Number(mo) - 1, 1).toLocaleString("en-IN", { month: "short", year: "2-digit" }); };

function SplitBar({ razorpay, phonepe, testid }) {
  const total = (razorpay || 0) + (phonepe || 0);
  const rz = total ? (razorpay / total) * 100 : 0;
  return (
    <div data-testid={testid} className="h-2.5 w-full rounded-full overflow-hidden bg-white/5 flex">
      <div style={{ width: `${rz}%`, background: RZ }} className="h-full transition-[width] duration-500" />
      <div style={{ width: `${total ? 100 - rz : 0}%`, background: PP }} className="h-full transition-[width] duration-500" />
    </div>
  );
}

export function RailAnalyticsCard({ api }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/payments/rails/analytics?months=12").then((r) => setData(r.data)).catch(() => setData({ months: [], purposes: [], totals: { razorpay: {}, phonepe: {} }, share: {} })); }, [api]);
  if (!data) return null;
  const t = data.totals;
  return (
    <section className="admin-card" data-testid="rail-analytics-card">
      <SectionHead kicker="Rail analytics · rail intents + shop orders" title="Razorpay vs PhonePe · collections" action={
        <span className="inline-flex items-center gap-4 text-[10px] font-grotesk uppercase tracking-[0.2em] text-[#888]">
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: RZ }} /> Razorpay</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: PP }} /> PhonePe</span>
        </span>} />
      <div className="grid md:grid-cols-3 gap-3 mb-5" data-testid="rail-analytics-totals">
        {[["razorpay", "Razorpay", RZ], ["phonepe", "PhonePe", PP]].map(([k, l, c]) => (
          <div key={k} data-testid={`rail-analytics-total-${k}`} className="border border-[#262626] rounded-sm p-4 bg-[#141414]">
            <div className="text-[10px] font-grotesk uppercase tracking-[0.24em]" style={{ color: c }}>{l} · {data.share?.[k] ?? 0}%</div>
            <div className="font-serif text-2xl mt-1 text-[#F5F5F5]">{inr(t[k]?.amount)}</div>
            <div className="text-xs text-[#888] mt-0.5">{t[k]?.count || 0} payments</div>
          </div>
        ))}
        <div className="border border-[#262626] rounded-sm p-4 bg-[#141414]">
          <div className="text-[10px] font-grotesk uppercase tracking-[0.24em] text-[#888]">Combined</div>
          <div className="font-serif text-2xl mt-1 text-[#F5F5F5]">{inr((t.razorpay?.amount || 0) + (t.phonepe?.amount || 0))}</div>
          <SplitBar razorpay={t.razorpay?.amount} phonepe={t.phonepe?.amount} testid="rail-analytics-total-split" />
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div data-testid="rail-analytics-months">
          <div className="text-[10px] font-grotesk uppercase tracking-[0.24em] text-[#C9A84C] mb-3 inline-flex items-center gap-2"><BarChart3 size={12} /> By month</div>
          {data.months.length === 0 && <div className="text-xs text-[#888]">No paid collections yet.</div>}
          <div className="space-y-2.5">
            {data.months.map((m) => (
              <div key={m.month} data-testid={`rail-analytics-month-${m.month}`} className="grid grid-cols-[64px_1fr_auto] items-center gap-3 text-xs">
                <span className="font-grotesk uppercase tracking-[0.14em] text-[#D4D4D4]">{monthLabel(m.month)}</span>
                <SplitBar razorpay={m.razorpay.amount} phonepe={m.phonepe.amount} />
                <span className="text-[#888] whitespace-nowrap"><span style={{ color: RZ }}>{inr(m.razorpay.amount)}</span> · <span style={{ color: PP }}>{inr(m.phonepe.amount)}</span></span>
              </div>
            ))}
          </div>
        </div>
        <div data-testid="rail-analytics-purposes">
          <div className="text-[10px] font-grotesk uppercase tracking-[0.24em] text-[#C9A84C] mb-3">By purpose</div>
          {data.purposes.length === 0 && <div className="text-xs text-[#888]">No paid collections yet.</div>}
          <div className="space-y-2.5">
            {data.purposes.map((p) => (
              <div key={p.purpose} data-testid={`rail-analytics-purpose-${p.purpose}`} className="text-xs">
                <div className="flex items-center justify-between gap-3 mb-1">
                  <span className="text-[#D4D4D4]">{p.label}</span>
                  <span className="text-[#888] whitespace-nowrap"><span style={{ color: RZ }}>{p.razorpay.count}</span> / <span style={{ color: PP }}>{p.phonepe.count}</span> · {inr(p.razorpay.amount + p.phonepe.amount)}</span>
                </div>
                <SplitBar razorpay={p.razorpay.amount} phonepe={p.phonepe.amount} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
